import { useId, type ReactNode } from 'react';
import { Select, SetupHeading, TokenInput, UsageMeter, Icon } from '@forecastify/ui';
import { useT, type MessageKey, type Translate } from '../../i18n';
import {
  addressInitials,
  classifyAddress,
  splitAddresses,
  type AddressVerdict,
} from './setup-rules';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Setup step 3 — invite the team: addresses, one permission each, seats.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The onboarding mockup's step 3, minus *"copy invite link"*, which the product owner dropped —
 * there is no such link in the product. A permission for everyone, which any chip can override;
 * a seat counter against the plan; and the one person who can never be invited here, yourself.
 *
 * ⚠️ **Owner is not offered** (ruled 13 Oct 2026): the four levels are the mockup's, and they map
 * to the built-in roles by code, so a renamed role still lands on the right one.
 */
export const LEVELS = ['ADMIN', 'PM', 'MEMBER', 'EXT'] as const;
export type Level = (typeof LEVELS)[number];

const LEVEL_LABEL: Readonly<Record<Level, MessageKey>> = {
  ADMIN: 'setup.invite.levelAdmin',
  PM: 'setup.invite.levelPm',
  MEMBER: 'setup.invite.levelMember',
  EXT: 'setup.invite.levelExt',
};

const REASON_LABEL: Readonly<
  Record<Extract<AddressVerdict, { status: 'invalid' }>['reason'], MessageKey>
> = {
  at: 'setup.invite.errAt',
  incomplete: 'setup.invite.errIncomplete',
  char: 'setup.invite.errChar',
  domain: 'setup.invite.errDomain',
};

/** Identity hues for the chips' initials — the tokens, as the preview uses them. */
const HUES = [
  'bg-[var(--id-1)] text-[var(--on-id)]',
  'bg-[var(--id-2)] text-[var(--on-id)]',
  'bg-[var(--id-3)] text-[var(--on-id)]',
  'bg-[var(--id-4)] text-[var(--on-id)]',
  'bg-[var(--id-5)] text-[var(--on-id)]',
];

export interface Chip {
  readonly id: string;
  readonly email: string;
  /** `null` follows the level for everyone. */
  readonly level: Level | null;
  readonly verdict: AddressVerdict;
}

export interface InviteValues {
  readonly chips: readonly Chip[];
  readonly draft: string;
  readonly level: Level;
}

export const EMPTY_INVITES: InviteValues = { chips: [], draft: '', level: 'MEMBER' };

let serial = 0;

/** Text into chips: split, deduplicated against what is there, each given a verdict. */
export function addChips(
  values: InviteValues,
  text: string,
  self: string,
  members: ReadonlySet<string>,
): InviteValues {
  const have = new Set(values.chips.map((chip) => chip.email));
  const added: Chip[] = [];
  for (const email of splitAddresses(text)) {
    if (have.has(email)) continue;
    have.add(email);
    serial += 1;
    added.push({
      id: `chip-${String(serial)}`,
      email,
      level: null,
      verdict: classifyAddress(email, self, members),
    });
  }
  return { ...values, chips: [...values.chips, ...added], draft: '' };
}

/** The addresses an invitation will actually go to. */
export function invitable(values: InviteValues): readonly Chip[] {
  return values.chips.filter((chip) => chip.verdict.status === 'ok');
}

/**
 * Why the step cannot send yet, or `null` — the mockup's `blockedKey` for step 3.
 *
 * @param free seats left before this step, or `null` for an unlimited plan.
 */
export function inviteBlocked(
  values: InviteValues,
  free: number | null,
  t: Translate,
): string | null {
  if (values.chips.some((chip) => chip.verdict.status === 'invalid')) {
    return t('setup.invite.blockedInvalid');
  }
  const count = invitable(values).length;
  if (free !== null && count > free) return t('setup.invite.blockedOver');
  if (count === 0) return t('setup.invite.blockedEmpty');
  return null;
}

export function InviteStep({
  values,
  onChange,
  self,
  members,
  seats,
  failures,
}: {
  readonly values: InviteValues;
  readonly onChange: (next: InviteValues) => void;
  readonly self: string;
  readonly members: ReadonlySet<string>;
  /** In use (people and open invitations) and the plan's limit; `null` limit is unlimited. */
  readonly seats: { readonly used: number; readonly limit: number | null } | null;
  /** Addresses a previous send could not reach. */
  readonly failures: readonly string[];
}): ReactNode {
  const t = useT();
  const fieldId = useId();
  const levelOptions = LEVELS.map((level) => ({ value: level, label: t(LEVEL_LABEL[level]) }));
  const okChips = invitable(values);
  const shown = values.chips.filter((chip) => chip.verdict.status !== 'invalid');
  const ignored = values.chips
    .filter((chip) => chip.verdict.status === 'self' || chip.verdict.status === 'member')
    .map((chip) => chip.email);
  const used = seats === null ? 0 : seats.used + okChips.length;
  const limit = seats?.limit ?? null;

  return (
    <>
      <SetupHeading title={t('setup.invite.title')} subtitle={t('setup.invite.subtitle')} />
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={fieldId} className="text-muted-foreground text-[12px] font-medium">
            {t('setup.invite.emails')}
          </label>
          <TokenInput
            id={fieldId}
            autoFocus
            draft={values.draft}
            placeholder={
              values.chips.length === 0
                ? t('setup.invite.emailsPlaceholder')
                : t('setup.invite.emailsMore')
            }
            onDraftChange={(draft) => {
              onChange({ ...values, draft });
            }}
            onCommit={(text) => {
              onChange(addChips(values, text, self, members));
            }}
            onRemove={(id) => {
              onChange({ ...values, chips: values.chips.filter((chip) => chip.id !== id) });
            }}
            tokens={values.chips.map((chip) => {
              const status = chip.verdict.status;
              const level = chip.level ?? values.level;
              return {
                id: chip.id,
                label: chip.email,
                initials: status === 'invalid' ? '!' : addressInitials(chip.email),
                tone: status === 'ok' ? 'ok' : status === 'invalid' ? 'bad' : 'muted',
                color: HUES[shown.indexOf(chip) % HUES.length],
                ...(chip.verdict.status === 'invalid'
                  ? { title: t(REASON_LABEL[chip.verdict.reason]) }
                  : {}),
                ...(status === 'self'
                  ? { tag: t('setup.invite.tagSelf') }
                  : status === 'member'
                    ? { tag: t('setup.invite.tagMember') }
                    : {}),
                ...(status === 'ok'
                  ? {
                      trailing: (
                        <Select
                          inline
                          aria-label={t('setup.invite.levelFor', { email: chip.email })}
                          className={
                            chip.level === null
                              ? 'text-muted-foreground h-6 w-auto shrink-0 text-[12px]'
                              : 'text-primary h-6 w-auto shrink-0 text-[12px] font-medium'
                          }
                          value={level}
                          options={levelOptions}
                          onChange={(event) => {
                            const next = LEVELS.find((entry) => entry === event.target.value);
                            if (next === undefined) return;
                            onChange({
                              ...values,
                              chips: values.chips.map((one) =>
                                one.id === chip.id
                                  ? { ...one, level: next === values.level ? null : next }
                                  : one,
                              ),
                            });
                          }}
                        />
                      ),
                    }
                  : {}),
                removeLabel: t('setup.invite.removeChip', { email: chip.email }),
              };
            })}
          />
          {values.chips.flatMap((chip) =>
            chip.verdict.status === 'invalid'
              ? [
                  <span key={chip.id} className="text-destructive text-[12px] leading-[18px]">
                    <span className="font-medium">{chip.email}</span> ·{' '}
                    {t(REASON_LABEL[chip.verdict.reason])}
                  </span>,
                ]
              : [],
          )}
          {ignored.length === 0 ? null : (
            <span className="text-muted-foreground text-[12px] leading-[18px]">
              {t('setup.invite.memberNote', { list: ignored.join(', ') })}
            </span>
          )}
          {failures.length === 0 ? null : (
            <span role="alert" className="text-destructive text-[12px] leading-[18px]">
              {t('setup.invite.failed', { list: failures.join(', ') })}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
            <span className="text-muted-foreground text-[12px] font-medium">
              {t('setup.invite.level')}
            </span>
            <Select
              aria-label={t('setup.invite.level')}
              icon="shield-check"
              value={values.level}
              options={levelOptions}
              onChange={(event) => {
                const next = LEVELS.find((entry) => entry === event.target.value);
                if (next === undefined) return;
                /* A chip that had chosen the new default now simply follows it. */
                onChange({
                  ...values,
                  level: next,
                  chips: values.chips.map((chip) =>
                    chip.level === next ? { ...chip, level: null } : chip,
                  ),
                });
              }}
            />
          </div>
          {limit === null ? null : (
            <UsageMeter
              text={t('setup.invite.seats', { n: used, m: limit })}
              used={used}
              limit={limit}
            />
          )}
        </div>
        {limit !== null && used > limit ? (
          <div className="-mt-1 flex items-start gap-2 rounded-md bg-[var(--status-warn-soft)] px-3 py-2.5 text-[12.5px] leading-[18px] text-[var(--status-warn-text)]">
            <Icon name="triangle-alert" size={14} className="mt-0.5 shrink-0" />
            <span className="text-pretty">
              {t('setup.invite.over', { k: used - limit, m: limit })}
            </span>
          </div>
        ) : null}
      </div>
    </>
  );
}
